import {
    Button,
    Card,
    Dropdown,
    DropdownContent,
    DropdownItem,
    DropdownTrigger,
    FilterButton,
    Input,
    Select,
    SelectElement,
    Tooltip,
} from "@jhool-io/fe-components";
import React from "react";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { Controller, useForm } from "react-hook-form";
import { useIsMutating } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import {
    addPracticeParamToUrl,
    convertArrayToString,
    isSmallScreen,
    removeEnumUnderscore,
    showMoneyInAppFormat,
    truncateString,
} from "../../../../utils/helpers";
import {
    CalculateFeesCustomerStatus,
    CalculateFeesInsuranceProviders,
    ICalculateClientFee,
    ICalculateFeeResponse,
    IPsychFeeResponse,
    ISubmitCalculateClientFee,
} from "../../types/tools.types";
import useToast from "../../../../hooks/useToast";
import {
    useONNCalculateClientFees,
    usePsychCalculateClientFees,
} from "../../hooks/mutations/tools.mutations";
import { useFetchClientList } from "../../../../hooks/queries/client";
import { useDebounce } from "../../../../hooks/helpers";
import {
    useFetchClientFees,
    useFetchClientInsuranceHistory,
} from "../../../clients/hooks/clients.queries";
import { copyToClipboard } from "../../../../utils/helpers/copy";
import CopyIcon from "../../../../components/Icons/Copy";
import { useAppSelector } from "../../../../hooks/useRedux";
import ArrowUpRightIcon from "../../../../icons/ArrowUpRight";

type Options = {
    label: string;
    value: string;
};

type FeeTypes =
    | "doctoral"
    | "masters"
    | "followup"
    | "initial"
    | "testing"
    | "psych";

// Select options for customer status
const customerStatusForSelectOptions = Object.values(
    CalculateFeesCustomerStatus
).map((status) => ({
    label: status,
    value: status,
}));

// finding insurance providers that match
const findMatchingOption = (
    selectedProvider: string,
    options: Options[]
): string | undefined => {
    const lowerCasedSelectedProvider = selectedProvider?.toLowerCase();

    // Try to find an exact match first
    const exactMatch = options?.find(
        (option) => option?.label?.toLowerCase() === lowerCasedSelectedProvider
    );

    if (exactMatch) return exactMatch.label;

    // Go back to partial word match
    const foundOption = options?.find((option) =>
        lowerCasedSelectedProvider
            ?.split(" ")
            ?.some((word) => option?.label?.toLowerCase()?.includes(word))
    );

    return foundOption?.label;
};

const schema = yup.object({
    customer_status: yup.string(),
    insurance: yup.string(),
    deductible: yup.string(),
    deductible_met: yup.string(),
    inn_coinsurance: yup.string(),
    inn_copay: yup.string(),
    oon_coinsurance: yup.string(),
    oon_copay: yup.string(),
    ucr: yup.string(),
});

export default function FeesCalculator() {
    const [feeResponse, setFeeResponse] = React.useState<
        ICalculateFeeResponse[]
    >([]);
    const [psychFeeResponse, setPsychFeeResponse] = React.useState<
        IPsychFeeResponse[]
    >([]);
    const [searchValue, setSearchValue] = React.useState("");
    const [showOptions, setShowOptions] = React.useState(false);
    const [selectedClient, setSelectedClient] = React.useState<Options | null>(
        null
    );
    const [feeCopied, setFeeCopied] = React.useState<FeeTypes | "all" | null>(
        null
    );
    const [selectedInsurance, setSelectedInsurance] = React.useState(false);
    const [selectedInsuranceProvider, setSelectedInsuranceProvider] =
        React.useState("");

    const selectRef = React.useRef<SelectElement | null>(null);
    const isCalculateMutating = useIsMutating(["calculate-fee"]);

    // Toast for success and error states
    const { toast } = useToast();

    const debouncedValue = useDebounce(searchValue, 500);

    const { practice } = useAppSelector((state) => state.userPractice);

    const { data, isLoading } = useFetchClientList(
        {
            page: 1,
            search_string: debouncedValue.toLowerCase(),
            provider_id: practice?.provider_id || "",
            sort_attribute: "client_name",
            sort_order: "ASC",
        },
        showOptions && searchValue.length > 0
    );

    // Fetch client insurance history
    const { data: insuranceProviders } = useFetchClientInsuranceHistory(
        selectedClient?.value || "",
        1,
        50,
        Boolean(selectedClient)
    );

    const { data: clientFees } = useFetchClientFees(
        selectedClient?.value as string,
        3,
        Boolean(selectedClient)
    );
    // Hook for adding client fee
    const onnFeesCalculate = useONNCalculateClientFees();
    const psychFeesCalculate = usePsychCalculateClientFees();

    const ClientNamesForSelect = data?.data.map((client) => ({
        label: `${client.first_name} ${client.last_name}`,
        value: client.client_id,
    }));

    // Get filtered options for clients list
    const filteredClients = ClientNamesForSelect?.filter((option) =>
        option.label.toLowerCase().includes(searchValue.toLowerCase())
    );

    const userCurrentProvider = insuranceProviders?.data.filter(
        (item) => item.status === "active"
    );

    const {
        register,
        handleSubmit,
        watch,
        control,
        setValue,
        formState: { errors },
    } = useForm<ICalculateClientFee>({
        resolver: yupResolver(schema),
        mode: "onChange",
    });

    const currentCustomerStatus = watch("customer_status");
    const currentPractice = practice?.practice_name as string;
    const currentFee = clientFees?.data?.[0];

    const deductibleToUse =
        currentPractice === "Park-Hill-Staging" ||
        currentPractice === "Park Hill"
            ? currentFee?.innded
            : currentFee?.oonded || "";

    // check to know if calculate fees button should be disabled
    const shouldDisableButton = () => {
        const deductible = watch("deductible");
        const deductibleMet = watch("deductible_met");
        const innCoinsurance = watch("inn_coinsurance");
        const innCopay = watch("inn_copay");
        const insurance = watch("insurance");
        const oonCoinsurance = watch("oon_coinsurance");
        const ucr = watch("ucr");

        // Check if all fields are empty or if insurance is empty
        const allFieldsEmpty =
            !deductible &&
            !deductibleMet &&
            !innCoinsurance &&
            !innCopay &&
            !insurance &&
            !oonCoinsurance &&
            !ucr;
        const insuranceEmpty = !selectedInsuranceProvider;

        // Return true if all fields are empty or if only insurance is empty
        return allFieldsEmpty || insuranceEmpty;
    };

    const insuranceToExclude = [
        CalculateFeesInsuranceProviders.BeaconGHI,
        CalculateFeesInsuranceProviders.BeaconGHINational,
        CalculateFeesInsuranceProviders.HarvardPilgrim,
        CalculateFeesInsuranceProviders.NYSHIP,
    ];

    // Select options for insurance providers
    const insuranceProvidersSelectOptions = [
        ...Object.values(CalculateFeesInsuranceProviders)
            .sort((a, b) => a.localeCompare(b))
            .filter((provider) => !insuranceToExclude.includes(provider))
            .map((provider) => ({
                label: provider,
                value: provider,
            })),
    ];

    const getInsuranceTextToDisplay = findMatchingOption(
        selectedInsuranceProvider,
        insuranceProvidersSelectOptions
    );

    React.useEffect(() => {
        if (!selectedInsurance) {
            setSelectedInsuranceProvider(
                userCurrentProvider?.[0]?.name?.toUpperCase() as string
            );
        }
    }, [selectedClient, selectedInsurance, userCurrentProvider]);

    const clearFields = () => {
        setValue("customer_status", CalculateFeesCustomerStatus.NEW);
        setValue("deductible", "");
        setValue("deductible_met", "");
        setValue("inn_coinsurance", "");
        setValue("inn_copay", "");
        setValue("oon_coinsurance", "");
        setValue("oon_copay", "");
        setValue("ucr", "");
        setSelectedInsuranceProvider("");
        setSelectedClient(null);
        setFeeResponse([]);
        setPsychFeeResponse([]);
    };

    // Update form values when currentFee is available
    React.useEffect(() => {
        if (selectedClient && currentFee) {
            setValue("deductible", deductibleToUse || "");
            setValue("inn_coinsurance", currentFee?.inncoins || "");
            setValue("inn_copay", currentFee?.innco || "");
            setValue("oon_coinsurance", currentFee?.ooncoins || "");
            setValue("oon_copay", currentFee?.oonco || "");
            setValue("ucr", currentFee?.ucr || "");
        }
    }, [currentFee, setValue, selectedClient, deductibleToUse]);

    // Function for copying FEES

    const removeDollarSignFromFee = (fee: string) => {
        if (!fee) {
            return 0;
        }
        return fee.replace(/\$/g, "");
    };

    const handleFeeCopied = (feeType: FeeTypes | "all") => {
        let feeToCopy: string | number;

        switch (feeType) {
            case "masters": {
                feeToCopy =
                    feeResponse.length > 0 && feeResponse[0]?.masters
                        ? removeDollarSignFromFee(feeResponse[0].masters)
                        : 0;
                const mastersText = convertArrayToString([`${feeToCopy}`]);
                copyToClipboard(mastersText);
                setFeeCopied("masters");
                break;
            }

            case "doctoral": {
                feeToCopy =
                    feeResponse.length > 0 && feeResponse[0]?.doctoral
                        ? removeDollarSignFromFee(feeResponse[0].doctoral)
                        : 0;
                const doctoralText = convertArrayToString([`${feeToCopy}`]);
                copyToClipboard(doctoralText);
                setFeeCopied("doctoral");
                break;
            }

            case "followup": {
                feeToCopy =
                    psychFeeResponse.length > 0 && psychFeeResponse[0]?.followup
                        ? removeDollarSignFromFee(
                              showMoneyInAppFormat(psychFeeResponse[0].followup)
                          )
                        : 0;
                const followupText = convertArrayToString([`${feeToCopy}`]);
                copyToClipboard(followupText);
                setFeeCopied("followup");
                break;
            }

            case "initial": {
                feeToCopy =
                    psychFeeResponse.length > 0 && psychFeeResponse[0]?.initial
                        ? removeDollarSignFromFee(
                              showMoneyInAppFormat(psychFeeResponse[0].initial)
                          )
                        : 0;
                const initialText = convertArrayToString([`${feeToCopy}`]);
                copyToClipboard(initialText);
                setFeeCopied("initial");
                break;
            }

            case "testing": {
                feeToCopy =
                    psychFeeResponse.length > 0 && psychFeeResponse[0]?.testing
                        ? removeDollarSignFromFee(
                              showMoneyInAppFormat(psychFeeResponse[0].testing)
                          )
                        : 0;
                const testingText = convertArrayToString([`${feeToCopy}`]);
                copyToClipboard(testingText);
                setFeeCopied("testing");
                break;
            }

            case "psych": {
                feeToCopy =
                    psychFeeResponse.length > 0 &&
                    psychFeeResponse[0]?.formatted
                        ? removeDollarSignFromFee(psychFeeResponse[0].formatted)
                        : 0;
                const testingText = convertArrayToString([`${feeToCopy}`]);
                copyToClipboard(testingText);
                setFeeCopied("psych");
                break;
            }

            case "all": {
                const mastersFee =
                    feeResponse.length > 0 && feeResponse[0]?.masters
                        ? removeDollarSignFromFee(feeResponse[0].masters)
                        : 0;
                const doctoralFee =
                    feeResponse.length > 0 && feeResponse[0]?.doctoral
                        ? removeDollarSignFromFee(feeResponse[0].doctoral)
                        : 0;
                // const followupFee =
                //     psychFeeResponse.length > 0 && psychFeeResponse[0]?.followup
                //         ? showMoneyInAppFormat(psychFeeResponse[0].followup)
                //         : showMoneyInAppFormat(0);
                // const initialFee =
                //     psychFeeResponse.length > 0 && psychFeeResponse[0]?.initial
                //         ? showMoneyInAppFormat(psychFeeResponse[0].initial)
                //         : showMoneyInAppFormat(0);
                const testingFee =
                    psychFeeResponse.length > 0 && psychFeeResponse[0]?.testing
                        ? removeDollarSignFromFee(
                              showMoneyInAppFormat(psychFeeResponse[0].testing)
                          )
                        : 0;
                const psychFee =
                    psychFeeResponse.length > 0 &&
                    psychFeeResponse[0]?.formatted
                        ? removeDollarSignFromFee(psychFeeResponse[0].formatted)
                        : 0;

                const feeText = convertArrayToString([
                    `${mastersFee}`,
                    `${doctoralFee}`,
                    `${psychFee}`,
                    `${testingFee}`,
                ]);
                copyToClipboard(feeText);
                setFeeCopied("all");
                break;
            }

            default:
                return;
        }

        setTimeout(() => {
            setFeeCopied(null);
        }, 2000);
    };

    const onSubmit = (payload: ICalculateClientFee) => {
        // Ensuring the correct matched insurance provider is sent to the backend
        const matchedOption = selectedInsuranceProvider
            ? findMatchingOption(
                  selectedInsuranceProvider,
                  insuranceProvidersSelectOptions
              )
            : null;

        const dataToSendOnn: ISubmitCalculateClientFee = {
            fee_params: [
                {
                    ...payload,
                    customer_status:
                        payload?.customer_status?.toLowerCase() || "initial",
                    insurance: matchedOption || "",
                },
            ],
        };

        const dataToSendPsych: ISubmitCalculateClientFee = {
            fee_params: [
                {
                    ...payload,
                    customer_status:
                        payload?.customer_status?.toLowerCase() || "initial",
                    insurance: matchedOption || "",
                },
            ],
        };

        onnFeesCalculate.mutate(dataToSendOnn, {
            onSuccess: (res) => {
                setFeeResponse(res.data);
            },
            onError: (err) => {
                toast({
                    mode: "error",
                    message:
                        err.response?.data.message ||
                        "Could not calculate ONN fees at this time",
                });
            },
        });

        psychFeesCalculate.mutate(dataToSendPsych, {
            onSuccess: (res) => {
                setPsychFeeResponse(res.data);
            },
            onError: (err) => {
                toast({
                    mode: "error",
                    message:
                        err.response?.data.message ||
                        "Could not calculate PSYCH fees at this time",
                });
            },
        });
    };

    return (
        <div className="flex justify-center items-center">
            <Card classNames="p-0">
                <div className="flex justify-between px-8 py-20 items-center border-b [border-bottom:1px_solid_var(--color-stroke)] border-strokedark">
                    <p className="text-title font-semibold">Fees Calculator</p>
                    <div className="flex gap-x-3 items-center">
                        <Dropdown>
                            <DropdownTrigger asChild>
                                <FilterButton>
                                    {getInsuranceTextToDisplay
                                        ? truncateString(
                                              removeEnumUnderscore(
                                                  getInsuranceTextToDisplay
                                              ),
                                              5
                                          )
                                        : "Insurance"}
                                </FilterButton>
                            </DropdownTrigger>
                            <DropdownContent
                                align={isSmallScreen() ? "start" : "end"}
                                width={isSmallScreen() ? "250px" : "auto"}
                                maxHeight={216}
                                className="overflow-y-auto"
                            >
                                {insuranceProvidersSelectOptions
                                    .sort((a, b) =>
                                        a.label.localeCompare(b.label)
                                    )
                                    .map((provider) => (
                                        <DropdownItem
                                            isSelected={
                                                provider.label ===
                                                selectedInsuranceProvider
                                            }
                                            onClick={() => {
                                                setSelectedInsuranceProvider(
                                                    provider.label
                                                );
                                                setSelectedInsurance(true);
                                            }}
                                            key={provider.label}
                                        >
                                            {removeEnumUnderscore(
                                                provider.label
                                            ).toUpperCase()}
                                        </DropdownItem>
                                    ))}
                            </DropdownContent>
                        </Dropdown>

                        <Button variant="secondary" onClick={clearFields}>
                            Clear
                        </Button>
                        <Link
                            to={addPracticeParamToUrl(
                                "/tools/onn-fees-calculator",
                                practice?.practice_id
                            )}
                            aria-label="Open in new page"
                            className="underline link"
                            target="_blank"
                        >
                            <ArrowUpRightIcon className="size-5" />
                        </Link>
                    </div>
                </div>
                <div className="p-16">
                    <Card classNames="p-16 bg-[--card-bg-gray]">
                        <form
                            id="calculate-fees"
                            onSubmit={handleSubmit(onSubmit)}
                        >
                            <div>
                                <div className="fg fg-space-between two flex">
                                    <Controller
                                        name="customer_status"
                                        control={control}
                                        defaultValue={
                                            CalculateFeesCustomerStatus.NEW
                                        }
                                        render={({ field }) => (
                                            <Select
                                                label="Customer Status"
                                                options={
                                                    customerStatusForSelectOptions
                                                }
                                                value={customerStatusForSelectOptions.find(
                                                    (option) =>
                                                        option.value ===
                                                        field.value
                                                )}
                                                onChange={(val) =>
                                                    field.onChange(
                                                        (val as Options).value
                                                    )
                                                }
                                                hasError={
                                                    !!errors.customer_status
                                                }
                                                errorText={
                                                    errors.customer_status
                                                        ?.message
                                                }
                                            />
                                        )}
                                    />
                                    {currentCustomerStatus === "Existing" ? (
                                        <Select
                                            ref={selectRef}
                                            isSearchable
                                            filterOption={() => true}
                                            placeholder="Enter Client name"
                                            value={undefined}
                                            aria-label="Client name"
                                            label="Search by Client name"
                                            options={filteredClients}
                                            onChange={(val) => {
                                                setSelectedClient(
                                                    val as Options
                                                );
                                                setSelectedInsurance(false);
                                            }}
                                            onInputChange={(value) => {
                                                setShowOptions(true);
                                                setSearchValue(value);
                                            }}
                                            onBlur={() => {
                                                setShowOptions(false);
                                            }}
                                            defaultValue={undefined}
                                            menuIsOpen={searchValue.length > 0}
                                            noOptionsMessage={({
                                                inputValue,
                                            }) =>
                                                isLoading && inputValue
                                                    ? "Fetching clients..."
                                                    : "No options"
                                            }
                                        />
                                    ) : null}
                                </div>
                                <div className="fg fg-space-between two flex">
                                    <Input
                                        {...register("deductible")}
                                        label="Deductible"
                                        placeholder={showMoneyInAppFormat(0)}
                                        hasError={!!errors.deductible}
                                        errorText={errors.deductible?.message}
                                    />
                                    <Input
                                        {...register("deductible_met")}
                                        label="Deductible met"
                                        placeholder={showMoneyInAppFormat(0)}
                                        hasError={!!errors.deductible_met}
                                        errorText={
                                            errors.deductible_met?.message
                                        }
                                    />
                                </div>
                                <div className="fg fg-space-between two flex">
                                    <Input
                                        {...register("inn_coinsurance")}
                                        label="INNCOINS"
                                        placeholder={showMoneyInAppFormat(0)}
                                        hasError={!!errors.inn_coinsurance}
                                        errorText={
                                            errors.inn_coinsurance?.message
                                        }
                                    />
                                    <Input
                                        {...register("inn_copay")}
                                        label="INNCOPAY"
                                        placeholder={showMoneyInAppFormat(0)}
                                        hasError={!!errors.inn_copay}
                                        errorText={errors.inn_copay?.message}
                                    />
                                </div>
                                <div className="fg fg-space-between two flex">
                                    <Input
                                        {...register("oon_coinsurance")}
                                        label="OONCOINS"
                                        placeholder={showMoneyInAppFormat(0)}
                                        hasError={!!errors.oon_coinsurance}
                                        errorText={
                                            errors.oon_coinsurance?.message
                                        }
                                    />
                                    <Input
                                        {...register("oon_copay")}
                                        label="ONNCOPAY"
                                        placeholder={showMoneyInAppFormat(0)}
                                        hasError={!!errors.oon_copay}
                                        errorText={errors.oon_copay?.message}
                                    />
                                </div>
                                <div className="fg fg-space-between two flex">
                                    <Input
                                        {...register("ucr")}
                                        label="UCR"
                                        placeholder={showMoneyInAppFormat(0)}
                                        hasError={!!errors.ucr}
                                        errorText={errors.ucr?.message}
                                    />
                                </div>

                                <div className="mx-2">
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="499"
                                        height="4"
                                        viewBox="0 0 499 4"
                                        fill="none"
                                    >
                                        <path
                                            d="M0 2L499 1.99996"
                                            stroke="#E7E7E7"
                                            strokeWidth="4"
                                            strokeDasharray="2 2"
                                        />
                                    </svg>
                                </div>

                                <div className="flex mt-4 justify-between items-center">
                                    <p className="text-textSec text-base font-semibold">
                                        Masters Fee
                                    </p>
                                    <div className="flex gap-x-3">
                                        <div className="text-base font-semibold ml">
                                            {feeResponse?.[0]?.masters
                                                ? removeDollarSignFromFee(
                                                      feeResponse?.[0]?.masters
                                                  )
                                                : 0}
                                        </div>
                                        <div>
                                            <Tooltip
                                                content={
                                                    feeCopied === "masters"
                                                        ? "Masters fee copied!"
                                                        : "Copy Masters fee result"
                                                }
                                            >
                                                <Button
                                                    onClick={() =>
                                                        handleFeeCopied(
                                                            "masters"
                                                        )
                                                    }
                                                    aria-label="copyInnBtn"
                                                    variant="normal"
                                                    size="auto"
                                                    className="self-center"
                                                >
                                                    <CopyIcon />
                                                </Button>
                                            </Tooltip>
                                        </div>
                                    </div>
                                </div>

                                <div className="flex justify-between mt-4 items-center">
                                    <p className="text-textSec text-base font-semibold">
                                        Doctoral Fee
                                    </p>
                                    <div className="flex gap-x-3">
                                        <p className="text-base font-semibold">
                                            {feeResponse?.[0]?.doctoral
                                                ? removeDollarSignFromFee(
                                                      feeResponse?.[0]?.doctoral
                                                  )
                                                : 0}
                                        </p>
                                        <Tooltip
                                            content={
                                                feeCopied === "doctoral"
                                                    ? "Doctoral fee copied!"
                                                    : "Copy Doctoral fee result"
                                            }
                                        >
                                            <Button
                                                onClick={() =>
                                                    handleFeeCopied("doctoral")
                                                }
                                                aria-label="copyInnBtn"
                                                variant="normal"
                                                size="auto"
                                                className="self-center"
                                            >
                                                <CopyIcon />
                                            </Button>
                                        </Tooltip>
                                    </div>
                                </div>
                                <div className="flex justify-between mt-4 items-center">
                                    <p className="text-textSec text-base font-semibold">
                                        Psych Fee
                                    </p>
                                    <div className="flex gap-x-3">
                                        <p className="text-base font-semibold">
                                            {psychFeeResponse.length > 0 &&
                                            psychFeeResponse[0]?.formatted
                                                ? removeDollarSignFromFee(
                                                      psychFeeResponse[0]
                                                          .formatted
                                                  )
                                                : 0}
                                        </p>
                                        <Tooltip
                                            content={
                                                feeCopied === "psych"
                                                    ? "Psych fee copied!"
                                                    : "Copy Psych fee result"
                                            }
                                        >
                                            <Button
                                                onClick={() =>
                                                    handleFeeCopied("psych")
                                                }
                                                aria-label="copyInnBtn"
                                                variant="normal"
                                                size="auto"
                                                className="self-center"
                                            >
                                                <CopyIcon />
                                            </Button>
                                        </Tooltip>
                                    </div>
                                </div>
                                {/* <div className="flex justify-between mt-4 items-center">
                                    <p className="text-textSec text-base font-semibold">
                                        Initial Fee
                                    </p>
                                    <div className="flex gap-x-3">
                                        <p className="text-base font-semibold">
                                            {psychFeeResponse.length > 0 &&
                                            psychFeeResponse[0]?.initial
                                                ? showMoneyInAppFormat(
                                                      psychFeeResponse[0]
                                                          ?.initial
                                                  )
                                                : showMoneyInAppFormat(0)}
                                        </p>
                                        <Tooltip
                                            content={
                                                feeCopied === "initial"
                                                    ? "Initial fee copied!"
                                                    : "Copy Initial fee result"
                                            }
                                        >
                                            <Button
                                                onClick={() =>
                                                    handleFeeCopied("initial")
                                                }
                                                aria-label="copyInnBtn"
                                                variant="normal"
                                                size="auto"
                                                className="self-center"
                                            >
                                                <CopyIcon />
                                            </Button>
                                        </Tooltip>
                                    </div>
                                    </div> */}
                                <div className="flex justify-between mt-4 items-center">
                                    <p className="text-textSec text-base font-semibold">
                                        Testing Fee
                                    </p>
                                    <div className="flex gap-x-3">
                                        <p className="text-base font-semibold">
                                            {psychFeeResponse.length > 0 &&
                                            psychFeeResponse[0]?.testing
                                                ? removeDollarSignFromFee(
                                                      showMoneyInAppFormat(
                                                          psychFeeResponse[0]
                                                              .testing
                                                      )
                                                  )
                                                : 0}
                                        </p>
                                        <Tooltip
                                            content={
                                                feeCopied === "testing"
                                                    ? "Testing fee copied!"
                                                    : "Copy Testing fee result"
                                            }
                                        >
                                            <Button
                                                onClick={() =>
                                                    handleFeeCopied("testing")
                                                }
                                                aria-label="copyInnBtn"
                                                variant="normal"
                                                size="auto"
                                                className="self-center"
                                            >
                                                <CopyIcon />
                                            </Button>
                                        </Tooltip>
                                    </div>
                                </div>

                                <div className="flex justify-end mt-4 items-center">
                                    <div className="flex gap-x-3">
                                        <p className="text-sm font-semibold">
                                            Copy All Fees
                                        </p>
                                        <Tooltip content="Copy fee results">
                                            <Button
                                                onClick={() =>
                                                    handleFeeCopied("all")
                                                }
                                                aria-label="copyFeeBtn"
                                                variant="normal"
                                                size="auto"
                                                className="self-center"
                                            >
                                                <CopyIcon />
                                            </Button>
                                        </Tooltip>
                                    </div>
                                </div>
                            </div>
                            <div className="flex justify-end mt-6 gap-x-[--spacing-items]">
                                <Button
                                    disabled={
                                        isCalculateMutating > 0 ||
                                        shouldDisableButton()
                                    }
                                    type="submit"
                                >
                                    Calculate Fees
                                </Button>
                            </div>
                        </form>
                    </Card>
                </div>
            </Card>
        </div>
    );
}
